import { useEffect, useState } from "react";
import {
  TenantConfigSchema,
  type ServiceCapRecommendation,
  type TenantConfig,
} from "@bidi-handel/core/schemas";
import { api, ApiError } from "../../api/client";
import { useApi } from "../../hooks/useApi";
import { useTenant } from "../../app/TenantContext";

const DEFAULT_CONFIG: TenantConfig = TenantConfigSchema.parse({});

export function TenantConfigPage() {
  const { tenantId } = useTenant();
  const { state, refetch } = useApi(
    () => api.getTenantConfig(tenantId),
    [tenantId]
  );
  const statsRes = useApi(
    () => api.getMatchPolicyStats(tenantId),
    [tenantId]
  );
  const [draft, setDraft] = useState<TenantConfig>(DEFAULT_CONFIG);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Setze Draft wenn Server-Daten ankommen.
  useEffect(() => {
    if (state.status === "ok") {
      setDraft(state.data);
      setSaved(false);
    }
  }, [state.status, state.data]);

  function updateFirma<K extends keyof TenantConfig["firma"]>(
    key: K,
    value: TenantConfig["firma"][K]
  ) {
    setDraft((d) => ({ ...d, firma: { ...d.firma, [key]: value } }));
    setSaved(false);
  }

  function updateNichtAngeboten<K extends keyof TenantConfig["nichtAngeboten"]>(
    key: K,
    value: TenantConfig["nichtAngeboten"][K]
  ) {
    setDraft((d) => ({
      ...d,
      nichtAngeboten: { ...d.nichtAngeboten, [key]: value },
    }));
    setSaved(false);
  }

  function updateMatchPolicy<K extends keyof TenantConfig["matchPolicy"]>(
    key: K,
    value: TenantConfig["matchPolicy"][K]
  ) {
    setDraft((d) => ({
      ...d,
      matchPolicy: { ...d.matchPolicy, [key]: value },
    }));
    setSaved(false);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const validated = TenantConfigSchema.parse(draft);
      const saved = await api.putTenantConfig(tenantId, validated);
      setDraft(saved);
      setSaved(true);
      refetch();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { hint?: string; error?: string; issues?: unknown[] } | null;
        if (body?.issues) {
          setError(
            `Validation: ${body.issues.map((i: unknown) => JSON.stringify(i)).join(" · ")}`
          );
        } else {
          setError(body?.hint ?? body?.error ?? err.message);
        }
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(String(err));
      }
    } finally {
      setSaving(false);
    }
  }

  if (state.status === "loading")
    return <p className="text-sm text-slate-500">Lade Tenant-Config …</p>;
  if (state.status === "error")
    return (
      <ErrorBanner error={state.error.message} onRetry={refetch} />
    );

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <p className="mb-6 text-xs text-slate-500">
        Die Tenant-Config beeinflusst die Bepreisung (Marge auf alle
        Positionen) und die Firma-Daten im Anschreiben/Angebot-Export.
      </p>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Field label="Marge in Prozent" hint="0–500, Default auf alle Artikel">
          <input
            type="number"
            min={0}
            max={500}
            step={0.1}
            value={draft.margeProzent}
            onChange={(e) =>
              setDraft({ ...draft, margeProzent: Number(e.currentTarget.value) })
            }
            className="input w-full"
          />
        </Field>
        <Field label="Waehrung">
          <input
            type="text"
            value={draft.waehrung}
            onChange={(e) =>
              setDraft({ ...draft, waehrung: e.currentTarget.value })
            }
            className="input w-full"
          />
        </Field>
      </div>

      <div className="mt-6">
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
          Firma (fuer Anschreiben/Angebot)
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Name">
            <input
              type="text"
              value={draft.firma.name ?? ""}
              onChange={(e) => updateFirma("name", e.currentTarget.value || undefined)}
              className="input w-full"
            />
          </Field>
          <Field label="USt-ID">
            <input
              type="text"
              value={draft.firma.ustId ?? ""}
              onChange={(e) => updateFirma("ustId", e.currentTarget.value || undefined)}
              className="input w-full"
            />
          </Field>
          <Field label="Strasse">
            <input
              type="text"
              value={draft.firma.strasse ?? ""}
              onChange={(e) =>
                updateFirma("strasse", e.currentTarget.value || undefined)
              }
              className="input w-full"
            />
          </Field>
          <Field label="PLZ + Ort" hint="z.B. 26169 Friesoythe">
            <div className="flex gap-2">
              <input
                type="text"
                value={draft.firma.plz ?? ""}
                onChange={(e) =>
                  updateFirma("plz", e.currentTarget.value || undefined)
                }
                className="input w-24"
                placeholder="PLZ"
              />
              <input
                type="text"
                value={draft.firma.ort ?? ""}
                onChange={(e) =>
                  updateFirma("ort", e.currentTarget.value || undefined)
                }
                className="input flex-1"
                placeholder="Ort"
              />
            </div>
          </Field>
          <Field label="E-Mail">
            <input
              type="email"
              value={draft.firma.email ?? ""}
              onChange={(e) =>
                updateFirma("email", e.currentTarget.value || undefined)
              }
              className="input w-full"
            />
          </Field>
          <Field label="Telefon">
            <input
              type="text"
              value={draft.firma.telefon ?? ""}
              onChange={(e) =>
                updateFirma("telefon", e.currentTarget.value || undefined)
              }
              className="input w-full"
            />
          </Field>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
          Nicht angeboten — Auto-Vorbelegung beim Import
        </h3>
        <p className="mb-3 text-xs text-slate-500">
          Positionen, deren bester Match-Kandidat zu einer dieser Warengruppen
          gehört oder mit einem dieser Artikel-Code-Präfixe beginnt, werden
          beim Pipeline-Lauf automatisch als „nicht angeboten" markiert. Der
          Innendienst kann das pro Position im Match-Drawer überschreiben.
        </p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field
            label="Warengruppen"
            hint="Komma-getrennt, case-insensitive. Z.B. dach, fenster, sanitär"
          >
            <TagListInput
              value={draft.nichtAngeboten.warengruppen}
              onChange={(arr) => updateNichtAngeboten("warengruppen", arr)}
              placeholder="dach, fenster, sanitär"
            />
          </Field>
          <Field
            label="Artikel-Code-Präfixe"
            hint="Komma-getrennt, case-insensitive startsWith. Z.B. DACH-, FE-, SAN-"
          >
            <TagListInput
              value={draft.nichtAngeboten.artikelPraefixe}
              onChange={(arr) => updateNichtAngeboten("artikelPraefixe", arr)}
              placeholder="DACH-, FE-, SAN-"
            />
          </Field>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
          Match-Policy
        </h3>
        <p className="mb-3 text-xs text-slate-500">
          Wenn der LV-Originaltext Dienstleistungs-Verben enthält (z.B.
          „liefern und aufbringen"), wird der Match-Score gegen die
          Stammdaten gekappt — Stammdaten decken nur Material ab, der Lohn
          muss separat bepreist werden. Der Cap-Wert bestimmt die Strenge:
          niedriger Wert → strikt (Position landet bei PRÜFEN/NICHT
          GEEIGNET); höherer Wert → milde (PICK bleibt möglich).
        </p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field
            label="Service-Score-Cap"
            hint="0–100, Default 75. Empfehlung: starte mit 75 + beobachte User-Entscheidungen."
          >
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={draft.matchPolicy.serviceCapScore}
              onChange={(e) =>
                updateMatchPolicy(
                  "serviceCapScore",
                  Math.max(0, Math.min(100, Number(e.currentTarget.value) || 0))
                )
              }
              className="input w-full"
            />
          </Field>
        </div>

        {/* Telemetrie + Recommender */}
        {statsRes.state.status === "ok" && (
          <ServiceCapStatsPanel
            stats={statsRes.state.data}
            onApply={(value) => {
              updateMatchPolicy("serviceCapScore", value);
            }}
            onRefresh={statsRes.refetch}
          />
        )}
      </div>

      {error && (
        <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm">
          <p className="font-medium text-rose-800">Speichern fehlgeschlagen</p>
          <p className="mt-1 font-mono text-xs text-rose-700">{error}</p>
        </div>
      )}

      <div className="mt-6 flex items-center justify-end gap-3">
        {saved && (
          <span className="text-xs font-medium text-emerald-700">
            ✓ gespeichert
          </span>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
        >
          {saving ? "Speichere …" : "Speichern"}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-slate-500">
        {label}
      </span>
      {children}
      {hint && <span className="mt-0.5 block text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

function ServiceCapStatsPanel({
  stats,
  onApply,
  onRefresh,
}: {
  stats: ServiceCapRecommendation;
  onApply: (value: number) => void;
  onRefresh: () => void;
}) {
  const noData = stats.sampleSize === 0;
  const hasRecommendation = stats.recommendedCap !== null;
  return (
    <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600">
          Telemetrie & Empfehlung
        </h4>
        <button
          type="button"
          onClick={onRefresh}
          className="text-[11px] text-slate-500 hover:text-slate-800"
        >
          ↻ aktualisieren
        </button>
      </div>

      {noData ? (
        <p className="text-xs text-slate-500">
          Noch keine Telemetrie-Daten — lass die Pipeline laufen und
          entscheide gecappte Positionen im Match-Drawer (Übernehmen / Nicht
          anbieten). Die Empfehlung erscheint sobald 20+ Entscheidungen
          vorliegen.
        </p>
      ) : (
        <>
          <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-5">
            <StatTile label="Gesamt" value={stats.sampleSize} />
            <StatTile
              label="Entschieden"
              value={`${stats.decidedCount} / ${stats.sampleSize}`}
            />
            <StatTile label="Übernommen" value={stats.pickedCount} tone="ok" />
            <StatTile
              label="Ausgeschlossen"
              value={stats.excludedCount}
              tone="warn"
            />
            <StatTile
              label="Pick-Rate"
              value={`${(stats.pickedRatio * 100).toFixed(0)} %`}
            />
          </div>
          <div
            className={`rounded-md border p-3 text-xs ${
              hasRecommendation
                ? "border-amber-300 bg-amber-50"
                : "border-emerald-300 bg-emerald-50"
            }`}
          >
            <p
              className={
                hasRecommendation
                  ? "font-medium text-amber-900"
                  : "font-medium text-emerald-900"
              }
            >
              {hasRecommendation ? "Empfehlung" : "Status"}
            </p>
            <p
              className={`mt-1 ${
                hasRecommendation ? "text-amber-900" : "text-emerald-900"
              }`}
            >
              {stats.rationale}
            </p>
            {hasRecommendation && stats.recommendedCap !== null && (
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onApply(stats.recommendedCap!)}
                  className="rounded-md border border-amber-400 bg-white px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
                >
                  Cap auf {stats.recommendedCap} setzen
                </button>
                <span className="text-[11px] text-amber-700">
                  (Speichern nicht vergessen)
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone?: "ok" | "warn";
}) {
  const color =
    tone === "ok"
      ? "text-emerald-700"
      : tone === "warn"
        ? "text-amber-700"
        : "text-slate-800";
  return (
    <div className="rounded-md bg-white px-3 py-2 ring-1 ring-slate-200">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className={`text-base font-semibold ${color}`}>{value}</div>
    </div>
  );
}

function TagListInput({
  value,
  onChange,
  placeholder,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");

  function commit() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    // Comma-getrennte Eingabe direkt aufsplitten — User kann z.B.
    // "dach, fenster, sanitär" eintippen + Enter drücken.
    const parts = trimmed
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    const next = [...value];
    for (const p of parts) {
      if (!next.includes(p)) next.push(p);
    }
    onChange(next);
    setDraft("");
  }

  function removeAt(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2 py-2">
      {value.map((tag, idx) => (
        <span
          key={`${tag}-${idx}`}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-800"
        >
          {tag}
          <button
            type="button"
            onClick={() => removeAt(idx)}
            className="text-slate-500 hover:text-rose-700"
            aria-label={`${tag} entfernen`}
          >
            ×
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit();
          } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
            removeAt(value.length - 1);
          }
        }}
        onBlur={() => commit()}
        placeholder={value.length === 0 ? placeholder : undefined}
        className="min-w-[120px] flex-1 border-0 bg-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
      />
    </div>
  );
}

function ErrorBanner({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="rounded-md border border-rose-200 bg-rose-50 p-4">
      <p className="text-sm font-medium text-rose-800">Tenant-Config nicht ladbar</p>
      <p className="mt-1 font-mono text-xs text-rose-700">{error}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 rounded-md border border-rose-300 bg-white px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
      >
        Nochmal versuchen
      </button>
    </div>
  );
}
